import React, { useState } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  Avatar,
  Menu,
  MenuItem,
  Chip,
  Badge,
  Tooltip
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Campaign as NotificationsIcon,
  Assessment as AnalyticsIcon,
  Security as SecurityIcon,
  Support as SupportIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  SwapHoriz as SwitchIcon,
  Home as FamilyIcon,
  AdminPanelSettings as AdminIcon,
  PersonAdd as InviteIcon
} from '@mui/icons-material';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useProfile } from '../../contexts/ProfileContext';
import { isRootAdmin, hasSystemPermission, SYSTEM_PERMISSIONS } from '../../utils/systemPermissions';
import logger from '../../utils/logger';

const DRAWER_WIDTH = 260;

/**
 * SystemAdminLayout Component
 * Main layout for the System Admin Portal
 */
const SystemAdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, logout } = useAuth();
  const { profile } = useProfile();

  const [drawerOpen, setDrawerOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);

  const menuOpen = Boolean(anchorEl);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      logger.error('Error logging out:', error);
    }
  };

  const handleSwitchPortal = () => {
    // Switch to family view
    if (profile?.familyCode) {
      navigate('/');
    } else {
      alert('You are not a member of any family');
    }
  };

  // Navigation items based on permissions
  const navigationItems = [
    {
      label: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/system-admin/dashboard',
      permission: null // Everyone can access
    },
    {
      label: 'System Notifications',
      icon: <NotificationsIcon />,
      path: '/system-admin/notifications',
      permission: SYSTEM_PERMISSIONS.CREATE_SYSTEM_NOTIFICATIONS
    },
    {
      label: 'User Management',
      icon: <PeopleIcon />,
      path: '/system-admin/users',
      permission: SYSTEM_PERMISSIONS.VIEW_USERS
    },
    {
      label: 'Analytics',
      icon: <AnalyticsIcon />,
      path: '/system-admin/analytics',
      permission: SYSTEM_PERMISSIONS.VIEW_ANALYTICS
    },
    {
      label: 'Audit Logs',
      icon: <SecurityIcon />,
      path: '/system-admin/audit-logs',
      permission: SYSTEM_PERMISSIONS.VIEW_AUDIT_LOGS
    },
    {
      label: 'Support Tickets',
      icon: <SupportIcon />,
      path: '/system-admin/support',
      permission: SYSTEM_PERMISSIONS.VIEW_SUPPORT_TICKETS
    },
    {
      label: 'IT Invitations',
      icon: <InviteIcon />,
      path: '/system-admin/invitations',
      permission: SYSTEM_PERMISSIONS.MANAGE_USERS,
      rootOnly: true
    },
    {
      label: 'Settings',
      icon: <SettingsIcon />,
      path: '/system-admin/settings',
      permission: SYSTEM_PERMISSIONS.MANAGE_SERVICES
    }
  ];

  // Filter navigation items based on user permissions
  const visibleNavItems = navigationItems.filter(item => {
    if (item.rootOnly && !isRootAdmin(profile)) return false;
    if (!item.permission) return true; // No permission required
    return hasSystemPermission(profile, item.permission);
  });

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'grey.900' }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: 'grey.900',
          borderBottom: '1px solid rgba(255, 255, 255, 0.12)'
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>

          <AdminIcon sx={{ mr: 2, color: 'primary.main' }} />

          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            FamilyForLife - System Administration
          </Typography>

          {/* Root Admin Badge */}
          {isRootAdmin(profile) && (
            <Chip
              label="ROOT ADMIN"
              color="error"
              size="small"
              sx={{ mr: 2, fontWeight: 'bold' }}
            />
          )}

          {/* Portal Switcher */}
          {profile?.familyCode && (
            <Tooltip title="Switch to Family Portal">
              <IconButton
                color="inherit"
                onClick={handleSwitchPortal}
                sx={{ mr: 1 }}
              >
                <Badge badgeContent={<FamilyIcon fontSize="small" />} color="primary">
                  <SwitchIcon />
                </Badge>
              </IconButton>
            </Tooltip>
          )}

          {/* User Menu */}
          <IconButton
            color="inherit"
            onClick={handleMenuOpen}
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
              {currentUser?.email?.[0]?.toUpperCase() || 'A'}
            </Avatar>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={handleMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem disabled>
              <Typography variant="subtitle2">{currentUser?.email}</Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Drawer
        variant="persistent"
        open={drawerOpen}
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            bgcolor: 'grey.800',
            color: 'grey.100',
            borderRight: '1px solid rgba(255, 255, 255, 0.12)'
          }
        }}
      >
        <Toolbar /> {/* Spacer for AppBar */}

        <Box sx={{ overflow: 'auto', mt: 2 }}>
          <List>
            {visibleNavItems.map((item) => (
              <ListItem key={item.path} disablePadding>
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => navigate(item.path)}
                  sx={{
                    '&.Mui-selected': {
                      bgcolor: 'primary.dark',
                      '&:hover': {
                        bgcolor: 'primary.dark'
                      }
                    }
                  }}
                >
                  <ListItemIcon sx={{ color: 'inherit' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          ml: drawerOpen ? 0 : `-${DRAWER_WIDTH}px`,
          transition: (theme) =>
            theme.transitions.create('margin', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen
            }),
          bgcolor: 'grey.900',
          color: 'grey.100',
          minHeight: '100vh'
        }}
      >
        <Toolbar /> {/* Spacer for AppBar */}
        <Outlet />
      </Box>
    </Box>
  );
};

export default SystemAdminLayout;
